import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Dictionary, keyBy } from 'lodash';
import { Currency } from 'api/account';
import { FileAttachment } from 'api/attachments';
import { ProjectFinancialsContractType } from 'api/project';
import type { Filter } from 'components/molecules/filters/types';
import {
  InvoicedEntityTypes,
  InvoiceLineItemGroups,
} from 'components/organisms/invoices/shared-enums';
import type {
  TaxCalculationMethod,
  TaxLineItemType,
  TaxSubtotalDisplayType,
} from 'components/organisms/invoices/taxes/types';
import type {
  CommonGroupedExpenses,
  CommonGroupedTimeEntries,
  FinancialsBudgetMap,
  InvoicedEntity,
  InvoiceDetails,
  InvoiceProjectWithSelection,
  LineItemsLookupWithOrder,
  LineItemsState,
  ProjectWiseWorkConfig,
  ProjectWiseWorkConfigExpense,
  TaxDetails,
} from 'components/organisms/invoices/types';

export enum APIStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

interface IsInvoiceDirty {
  isCompanyDirty: boolean;
  isProjectDirty: Dictionary<boolean>;
  isWorkDirty: Dictionary<boolean>;
  workLineitemsDirty: Dictionary<Dictionary<boolean>>;
}
export interface InvoiceState {
  invoiceDetails: InvoiceDetails;
  /** When multi-budget is enabled, projects may include financialsBudgetMap per project. */
  projects: InvoiceProjectWithSelection[];
  work: Dictionary<ProjectWiseWorkConfig>;
  expenses: Dictionary<ProjectWiseWorkConfigExpense>;
  lineItems: Dictionary<LineItemsLookupWithOrder>;
  invoiceSourceMappingIdsOrder: number[];
  /** When multi-budget: projectId -> ordered budget ids for preview/modify-step. */
  invoiceBudgetIdsOrderByProject: Record<number, number[]>;
  notes?: string;
  attachments: FileAttachment[];
  currency?: Currency;
  isInvoiceDirty: IsInvoiceDirty;
  tax: number;
  taxCodeRef?: TaxDetails;
  fields: Dictionary<any>;
  invoiceTaxCalculationMethod?: TaxCalculationMethod;
  invoiceTaxLineItemType?: TaxLineItemType;
  invoiceTaxSubtotalDisplayType?: TaxSubtotalDisplayType;
  invoiceDisplayTitle?: string;
  /** When true, summary/preview shows project totals only (no per-budget breakdown). */
  hideBudgetBreakdown?: boolean;
  /** Persists the active filter on the select-projects step across step navigation. */
  selectProjectsFilter?: Filter;
}

const INITIAL_DATA: InvoiceState = {
  invoiceDetails: {} as InvoiceDetails,
  projects: [],
  work: {},
  expenses: {},
  lineItems: {},
  notes: undefined,
  attachments: [],
  currency: undefined,
  invoiceSourceMappingIdsOrder: [],
  invoiceBudgetIdsOrderByProject: {},
  isInvoiceDirty: {
    isCompanyDirty: false,
    isProjectDirty: {},
    isWorkDirty: {},
    workLineitemsDirty: {},
  },
  tax: 0,
  taxCodeRef: {
    taxCodeId: 0,
    taxCodeName: '',
    taxCodeRate: 0,
    taxComponents: [],
  },
  fields: {},
  hideBudgetBreakdown: false,
};

const EMPTY_OBJECT = {};

/** Line items key: projectId-budgetId when multi-budget (budgetId provided), else projectId. Backward compatible. */
function getLineItemKey(projectId: number, budgetId?: number): string {
  return budgetId != null ? `${projectId}-${budgetId}` : String(projectId);
}

export const invoiceSlice = createSlice({
  initialState: INITIAL_DATA,
  name: 'invoice',
  reducers: {
    setInvoiceState: (
      state: InvoiceState,
      { payload }: PayloadAction<Partial<InvoiceState>>,
    ) => {
      return { ...state, ...payload };
    },
    setAllProjects: (state: InvoiceState, { payload }) => {
      return {
        ...state,
        projects: payload.projects,
        isInvoiceDirty: {
          ...state.isInvoiceDirty,
          isCompanyDirty: false,
        },
      };
    },
    setSelectedProjectsAndCurrency: (
      state: InvoiceState,
      {
        payload,
      }: PayloadAction<{
        currency: Currency;
        projectIdsToBeSelected: number[];
        /** When multi-budget FF on: projectId -> selected projectFinancialsBudgetIds. Omit when FF off. */
        selectedBudgetIdsByProject?: Record<number, number[]>;
      }>,
    ) => {
      const hasBudgetSelection = payload.selectedBudgetIdsByProject != null;

      const selectedProjectIds = hasBudgetSelection
        ? new Set(
            (
              Object.entries(payload.selectedBudgetIdsByProject ?? {}) as [
                string,
                number[],
              ][]
            )
              .filter(([, ids]) => ids.length > 0)
              .map(([pid]) => Number(pid)),
          )
        : new Set(payload.projectIdsToBeSelected);

      const existingSelectedProjectMap = keyBy(
        state.projects.filter((x) => x.selected),
        'projectId',
      );

      const removedProjectIds = Object.keys(existingSelectedProjectMap).filter(
        (projId) => !selectedProjectIds.has(Number(projId)),
      );
      const newProjectIds = [...selectedProjectIds].filter(
        (projId) => existingSelectedProjectMap[String(projId)] == null,
      );

      const projectDirty = [...removedProjectIds, ...newProjectIds].reduce(
        (acc, curr) => {
          acc[curr] = true;

          return acc;
        },
        {} as Dictionary<boolean>,
      );

      // Also mark existing selected projects as dirty when their budget selection
      // changes (budget added or removed). Without this, adding a new budget to an
      // already-selected project would leave isProjectDirty empty, causing step 3
      // to skip the work/expense fetch for the newly selected budget.
      if (hasBudgetSelection) {
        Object.entries(payload.selectedBudgetIdsByProject ?? {}).forEach(
          ([pidStr, newIds]) => {
            const pid = Number(pidStr);

            // Skip projects already marked dirty (new or removed projects)
            if (projectDirty[pid]) {
              return;
            }
            const existing = existingSelectedProjectMap[pidStr];

            if (!existing) {
              return;
            }
            const existingSelectedIds = new Set(
              Object.values(existing.financialsBudgetMap ?? {})
                .filter((b) => b.selected)
                .map((b) => b.projectFinancialsBudgetId),
            );
            const newSet = new Set(newIds);
            const budgetSelectionChanged =
              newIds.some((id) => !existingSelectedIds.has(id)) ||
              [...existingSelectedIds].some((id) => !newSet.has(id));

            if (budgetSelectionChanged) {
              projectDirty[pid] = true;
            }
          },
        );
      }

      const selectedProjects = state.projects.map((x) => {
        const budgetIds: number[] | undefined = hasBudgetSelection
          ? payload.selectedBudgetIdsByProject?.[x.projectId]
          : undefined;
        const projectSelected = hasBudgetSelection
          ? (budgetIds?.length ?? 0) > 0
          : selectedProjectIds.has(x.projectId);

        let updatedMap: FinancialsBudgetMap | undefined = x.financialsBudgetMap;

        if (
          hasBudgetSelection &&
          updatedMap &&
          typeof updatedMap === 'object'
        ) {
          const selectedSet = new Set(budgetIds ?? []);

          updatedMap = Object.fromEntries(
            Object.entries(updatedMap).map(([k, b]) => [
              k,
              {
                ...b,
                selected: selectedSet.has(b.projectFinancialsBudgetId),
              },
            ]),
          ) as FinancialsBudgetMap;
        }

        return {
          ...x,
          selected: projectSelected,
          ...(updatedMap != null && { financialsBudgetMap: updatedMap }),
        };
      });

      const fProjDirty = {
        ...state.isInvoiceDirty.isProjectDirty,
        ...projectDirty,
      };

      return {
        ...state,
        projects: selectedProjects,
        currency: payload.currency,
        isInvoiceDirty: {
          isCompanyDirty: false,
          isWorkDirty: fProjDirty,
          workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty,
          isProjectDirty: fProjDirty,
        },
      };
    },
    setInvoiceBasicDetails: (
      state: InvoiceState,
      { payload }: PayloadAction<Partial<InvoiceState>>,
    ) => {
      const isSameCustomer =
        state.invoiceDetails.company?.companyId ===
          payload.invoiceDetails?.company?.companyId ||
        payload.invoiceDetails?.company?.companyId == null;

      return {
        ...state,
        invoiceDetails: {
          ...state.invoiceDetails,
          ...payload.invoiceDetails,
        },
        fields: {
          ...state.fields,
          ...payload.fields,
        },
        ...(!isSameCustomer
          ? {
              isInvoiceDirty: {
                ...state.isInvoiceDirty,
                isCompanyDirty: true,
              },
            }
          : {}),
      };
    },
    setAllProjectWork: (
      state: InvoiceState,
      { payload }: PayloadAction<Dictionary<ProjectWiseWorkConfig>>,
    ) => {
      // This is done so that when we reland on work page, not all projects data should be overriden, only project diff ka data should change.
      // Keys may be projectId (number) or "projectId-budgetId" (multi-budget).
      const projectDiff = Object.keys(payload).reduce((acc, curr) => {
        acc[curr] = state.work[curr] ?? payload[curr];

        return acc;
      }, {} as Dictionary<ProjectWiseWorkConfig>);

      return {
        ...state,
        work: projectDiff,
        isInvoiceDirty: {
          isCompanyDirty: false,
          isProjectDirty: EMPTY_OBJECT,
          isWorkDirty: state.isInvoiceDirty.isWorkDirty,
          workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty,
        },
      };
    },
    setAllProjectExpenses: (
      state: InvoiceState,
      { payload }: PayloadAction<Dictionary<ProjectWiseWorkConfigExpense>>,
    ) => {
      // This is done so that when we reland on work page, not all projects data should be overriden, only project diff ka data should change.
      // Keys may be projectId (number) or "projectId-budgetId" (multi-budget).
      const projectDiff = Object.keys(payload).reduce((acc, curr) => {
        acc[curr] = state.expenses[curr] ?? payload[curr];

        return acc;
      }, {} as Dictionary<ProjectWiseWorkConfigExpense>);

      return {
        ...state,
        expenses: projectDiff,
        isInvoiceDirty: {
          isCompanyDirty: false,
          isProjectDirty: EMPTY_OBJECT,
          isWorkDirty: state.isInvoiceDirty.isWorkDirty,
          workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty,
        },
      };
    },
    setProjectWork: (
      state: InvoiceState,
      {
        payload: { data, isWorkDirtyProjectId, workLineItemsDirty },
      }: PayloadAction<{
        data: Dictionary<ProjectWiseWorkConfig>;
        isWorkDirtyProjectId?: number;
        workLineItemsDirty?: Dictionary<Dictionary<boolean>>;
      }>,
    ) => {
      const workItemsDirty = workLineItemsDirty
        ? Object.keys(workLineItemsDirty).reduce((acc, curr) => {
            acc[curr] = {
              ...state.isInvoiceDirty.workLineitemsDirty[curr],
              ...workLineItemsDirty[curr],
            };

            return acc;
          }, {} as Dictionary<Dictionary<boolean>>)
        : {};

      return {
        ...state,
        work: {
          ...state.work,
          ...data,
        },
        isInvoiceDirty: {
          isCompanyDirty: false,
          isProjectDirty: EMPTY_OBJECT,
          isWorkDirty: isWorkDirtyProjectId
            ? {
                ...state.isInvoiceDirty.isWorkDirty,
                [isWorkDirtyProjectId]: true,
              }
            : state.isInvoiceDirty.isWorkDirty,
          workLineitemsDirty: {
            ...state.isInvoiceDirty.workLineitemsDirty,
            ...workItemsDirty,
          },
        },
      };
    },
    setProjectExpense: (
      state: InvoiceState,
      {
        payload: { data, isWorkDirtyProjectId, workLineItemsDirty },
      }: PayloadAction<{
        data: Dictionary<ProjectWiseWorkConfigExpense>;
        isWorkDirtyProjectId?: number;
        workLineItemsDirty?: Dictionary<Dictionary<boolean>>;
      }>,
    ) => {
      const workItemsDirty = workLineItemsDirty
        ? Object.keys(workLineItemsDirty).reduce((acc, curr) => {
            acc[curr] = {
              ...(state.isInvoiceDirty.workLineitemsDirty?.[curr] ?? {}),
              ...workLineItemsDirty[curr],
            };

            return acc;
          }, {} as Dictionary<Dictionary<boolean>>)
        : {};

      return {
        ...state,
        expenses: {
          ...state.expenses,
          ...data,
        },
        isInvoiceDirty: {
          isCompanyDirty: false,
          isProjectDirty: EMPTY_OBJECT,
          isWorkDirty: isWorkDirtyProjectId
            ? {
                ...state.isInvoiceDirty.isWorkDirty,
                [isWorkDirtyProjectId]: true,
              }
            : state.isInvoiceDirty.isWorkDirty,
          workLineitemsDirty: {
            ...state.isInvoiceDirty.workLineitemsDirty,
            ...workItemsDirty,
          },
        },
      };
    },
    setLineItems: (
      state: InvoiceState,
      {
        payload: { data },
      }: PayloadAction<{ data: Dictionary<LineItemsLookupWithOrder> }>,
    ) => {
      const updatedData = Object.keys(data).reduce((acc, curr) => {
        // so that we dont remove the added line items when we go back to the line details screens
        const manuallyAddedItems = Object.keys(
          state.lineItems[curr]?.lineDetails ?? {},
        ).reduce(
          (a, x) => {
            if (state.lineItems[curr]?.lineDetails[x].isManuallyAdded) {
              a.lineDetails = {
                ...a.lineDetails,
                [x]: state.lineItems[curr]?.lineDetails[x],
              };
              a.lineItemOrder = [...a.lineItemOrder, Number(x)];
            }

            return a;
          },
          {
            lineDetails: {},
            lineItemOrder: [],
          } as LineItemsLookupWithOrder,
        );

        acc[curr] = {
          lineItemOrder: [
            ...(data[curr].lineItemOrder ?? []),
            ...manuallyAddedItems.lineItemOrder,
          ],
          lineDetails: {
            ...data[curr].lineDetails,
            ...manuallyAddedItems.lineDetails,
          },
        };

        return acc;
      }, {} as Dictionary<LineItemsLookupWithOrder>);

      return {
        ...state,
        lineItems: { ...state.lineItems, ...updatedData },
        isInvoiceDirty: {
          ...state.isInvoiceDirty,
          isWorkDirty: EMPTY_OBJECT,
          workLineitemsDirty: EMPTY_OBJECT,
        },
      };
    },
    setSourceMappingOrder: (
      state: InvoiceState,
      {
        payload: { data, replace },
      }: PayloadAction<{ data: number[]; replace?: boolean }>,
    ) => {
      if (replace) {
        return {
          ...state,
          invoiceSourceMappingIdsOrder: [...data],
        };
      }
      const currentSet = new Set(state.invoiceSourceMappingIdsOrder);
      const incomingSet = new Set(data);
      const sameSet =
        currentSet.size === incomingSet.size &&
        data.every((id) => currentSet.has(id));

      if (sameSet) {
        return state;
      }
      // done so that order if set manually doesn't change when we add or remove new projects
      const excludingRemovedIds = state.invoiceSourceMappingIdsOrder.filter(
        (x) => data.includes(x),
      );
      const allSourceIds = [...excludingRemovedIds, ...data];
      const uniqSourceIds = [...new Set(allSourceIds)];

      return {
        ...state,
        invoiceSourceMappingIdsOrder: uniqSourceIds,
      };
    },
    setBudgetOrderByProject: (
      state: InvoiceState,
      { payload: { data } }: PayloadAction<{ data: Record<number, number[]> }>,
    ) => {
      return {
        ...state,
        invoiceBudgetIdsOrderByProject: {
          ...state.invoiceBudgetIdsOrderByProject,
          ...data,
        },
      };
    },
    deleteLineItem: (
      state: InvoiceState,
      {
        payload: { projectId, lineId, invoicedEntity, sourceId, budgetId },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        invoicedEntity: InvoicedEntity;
        sourceId: number;
        /** When multi-budget: scope to this budget. Omit for old UI. */
        budgetId?: number;
      }>,
    ) => {
      const lineKey = getLineItemKey(projectId, budgetId);
      const projectLineItems = state.lineItems[lineKey];

      if (!projectLineItems) {
        return state;
      }

      const updatedLineOrder = projectLineItems.lineItemOrder.filter(
        (x) => x !== lineId,
      );

      const currentProjectLineItemsState = {
        lineDetails: { ...projectLineItems.lineDetails },
        lineItemOrder: updatedLineOrder,
      };

      delete currentProjectLineItemsState.lineDetails[lineId];

      const workKey = getLineItemKey(projectId, budgetId);
      const projectTimeEntryState = state.work[workKey];

      // To handle the case where user is trying to delete a manually added line
      if (
        projectTimeEntryState?.childrenBreakdown?.[sourceId] == null &&
        state.expenses[workKey]?.childrenBreakdown?.[sourceId] == null
      ) {
        return {
          ...state,
          lineItems: {
            ...state.lineItems,
            [lineKey]: currentProjectLineItemsState,
          },
        };
      }
      let currentProjectWorkState = {
        ...projectTimeEntryState.childrenBreakdown,
      };

      if (invoicedEntity.invoicedEntityType === InvoicedEntityTypes.TimeEntry) {
        if (
          projectTimeEntryState.financialContractType ===
            ProjectFinancialsContractType.TM &&
          projectTimeEntryState.viewConfig.lineItemsGroupingBy !==
            InvoiceLineItemGroups.TimeEntries
        ) {
          const flipToUnselected = (
            projectTimeEntryState.childrenBreakdown[
              sourceId
            ] as CommonGroupedTimeEntries
          ).invoiceTimeEntryModels.map((x) => ({
            ...x,
            selected: false,
          }));

          currentProjectWorkState = {
            ...projectTimeEntryState.childrenBreakdown,
            [sourceId]: {
              ...projectTimeEntryState.childrenBreakdown[sourceId],
              invoiceTimeEntryModels: flipToUnselected,
            },
          };
        } else {
          const workEntry = {
            ...projectTimeEntryState.childrenBreakdown[sourceId],
            selected: false,
          };

          currentProjectWorkState = {
            ...projectTimeEntryState.childrenBreakdown,
            [sourceId]: workEntry,
          };
        }
      }

      const projectExpenseState = state.expenses[workKey];
      let currentProjectExpenseState = {
        ...projectExpenseState.childrenBreakdown,
      };

      if (invoicedEntity.invoicedEntityType === InvoicedEntityTypes.Expense) {
        if (
          projectExpenseState.viewConfig.lineItemsGroupingBy !==
          InvoiceLineItemGroups.Expense
        ) {
          const flipToUnselected = (
            projectExpenseState.childrenBreakdown[
              sourceId
            ] as CommonGroupedExpenses
          ).invoiceExpenseModels.map((x) => ({
            ...x,
            selected: false,
          }));

          currentProjectExpenseState = {
            ...projectExpenseState.childrenBreakdown,
            [sourceId]: {
              ...projectExpenseState.childrenBreakdown[sourceId],
              invoiceExpenseModels: flipToUnselected,
            },
          };
        } else {
          const expenseEntry = {
            ...projectExpenseState.childrenBreakdown[sourceId],
            selected: false,
          };

          currentProjectExpenseState = {
            ...projectExpenseState.childrenBreakdown,
            [sourceId]: expenseEntry,
          };
        }
      }

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [lineKey]: currentProjectLineItemsState,
        },
        work: {
          ...state.work,
          [workKey]: {
            ...state.work[workKey],
            childrenBreakdown: currentProjectWorkState,
          },
        },
        expenses: {
          ...state.expenses,
          [workKey]: {
            ...state.expenses[workKey],
            childrenBreakdown: currentProjectExpenseState,
          },
        },
      } as InvoiceState;
    },
    createLineItem: (
      state: InvoiceState,
      {
        payload: { projectId, lineId, taxCodeRef, budgetId },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        taxCodeRef?: TaxDetails;
        /** When multi-budget: scope new line to this budget. Omit for old UI. */
        budgetId?: number;
      }>,
    ) => {
      const key = getLineItemKey(projectId, budgetId);
      const existing = state.lineItems[key] ?? {
        lineDetails: {},
        lineItemOrder: [],
      };

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [key]: {
            lineItemOrder: existing.lineItemOrder.concat(lineId),
            lineDetails: {
              ...existing.lineDetails,
              [lineId]: {
                invoiceLineItemId: lineId,
                invoicedEntity: EMPTY_OBJECT,
                description: '',
                quantity: 0,
                unitPrice: 0,
                amount: 0,
                taxCodeRef,
                projectId,
                isNew: true,
                isManuallyAdded: true,
                price: 0,
              },
            },
          },
        },
      };
    },
    updateLineItem: (
      state: InvoiceState,
      {
        payload: { projectId, lineId, updates, budgetId },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        updates: Partial<LineItemsState>;
        /** When multi-budget: scope to this budget. Omit for old UI. */
        budgetId?: number;
      }>,
    ) => {
      const key = getLineItemKey(projectId, budgetId);
      const existing = state.lineItems[key];

      if (!existing) {
        return state;
      }

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [key]: {
            lineItemOrder: existing.lineItemOrder,
            lineDetails: {
              ...existing.lineDetails,
              [lineId]: {
                ...existing.lineDetails[lineId],
                ...updates,
              },
            },
          },
        },
      };
    },
    updateLineItemOrder: (
      state: InvoiceState,
      {
        payload: { projectId, newLineItemOrder, budgetId },
      }: PayloadAction<{
        projectId: number;
        newLineItemOrder: number[];
        /** When multi-budget: scope to this budget. Omit for old UI. */
        budgetId?: number;
      }>,
    ) => {
      const key = getLineItemKey(projectId, budgetId);
      const existing = state.lineItems[key];

      if (!existing) {
        return state;
      }

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [key]: {
            lineDetails: existing.lineDetails,
            lineItemOrder: newLineItemOrder,
          },
        },
      };
    },
    setFields: (
      state: InvoiceState,
      { payload }: PayloadAction<Dictionary<any>>,
    ) => {
      return { ...state, fields: { ...state.fields, ...payload } };
    },
    updateAllLineItemsTaxAndFinalAmount: (
      state: InvoiceState,
      {
        payload: { updatedTax, projectId, budgetId },
      }: PayloadAction<{
        updatedTax?: TaxDetails;
        projectId: number;
        /** When multi-budget: scope to this budget. Omit for old UI. */
        budgetId?: number;
      }>,
    ) => {
      const key = getLineItemKey(projectId, budgetId);
      const projectLineItems = state.lineItems[key];

      if (!projectLineItems) {
        return state;
      }

      const updatedLineItems: Dictionary<LineItemsLookupWithOrder> = {};
      const updatedLineDetails: Dictionary<LineItemsState> = {};

      Object.keys(projectLineItems.lineDetails).forEach((lineItemId) => {
        const lineItem = projectLineItems.lineDetails[lineItemId];
        const effectiveTax = updatedTax;
        const subtotal =
          lineItem.price ??
          (lineItem.unitPrice != null && lineItem.quantity != null
            ? lineItem.unitPrice * lineItem.quantity
            : lineItem.amount ?? 0);
        const amount =
          Math.round(
            (1 + (effectiveTax?.taxCodeRate ?? 0) / 100) *
              (subtotal || 0) *
              100,
          ) / 100;

        updatedLineDetails[lineItemId] = {
          ...lineItem,
          taxCodeRef: effectiveTax,
          amount,
        };
      });

      updatedLineItems[key] = {
        ...projectLineItems,
        lineDetails: updatedLineDetails,
      };

      return {
        ...state,
        lineItems: { ...state.lineItems, ...updatedLineItems },
        taxCodeRef: updatedTax,
      };
    },
    setSelectProjectsFilter: (
      state: InvoiceState,
      { payload }: PayloadAction<Filter | undefined>,
    ) => {
      return { ...state, selectProjectsFilter: payload };
    },
    resetAllData: () => {
      return INITIAL_DATA;
    },
  },
});
